<template>
    <div class="flex no-wrap" style="height: calc(100vh - 60px);">
    <div class="flex column no-wrap" :style="`position: relative; height: 100%; width: calc(100% - ${chatPanelWidth}); transition: width 0.15s ease-in-out;`">
        <q-resize-observer @resize="updateDashboardWidth"/>
        <q-scroll-area :thumb-style="thumbStyle" :horizontal-offset="[0, 4]" :content-style="scrollContentStyle" :content-active-style="scrollContentStyle" class="col full-width" style="background: #fafafa;">
            <!-- FILTERS -->
            <div style="border-bottom: 1px solid #dedede; background: #ffffff;">
                <div class="flex no-wrap items-end q-pt-sm q-pb-md q-pr-md" style="position: relative; width: calc(100% / 0.85); transform: scale(0.85); transform-origin: top left; z-index: 1000; margin-bottom: -10px;">
                    <template v-if="!loadingOptions">
                        <SingleSelect v-model="selectedBrand" :options="brandOptions" label="BRAND" placeholder="Select brand" style="width: 150px;" class="q-ml-md"/>
                        <MultiSelect :model-value="selectedStrategicImperatives" :options="strategicImperativeOptions" label="STRATEGIC IMPERATIVE" placeholder="Select imperatives" style="width: 230px;" class="q-ml-md" @update:model-value="selectStrategicImperatives"/>

                        <div class="cc-date-filter q-ml-md">
                            <div class="cc-date-filter__label">MONTH RANGE</div>
                            <button type="button" class="cc-date-filter__trigger" :class="{ 'cc-date-filter__trigger--placeholder': !selectedDateRange }">
                                <span class="cc-date-filter__value">{{ selectedDateRange ? formatMonthRange(selectedDateRange) : "Current cycle" }}</span>
                                <q-icon v-if="selectedDateRange" name="close" size="16px" color="grey-7" class="cc-date-filter__clear" @click.stop="selectedDateRange = null">
                                    <q-tooltip>Clear months</q-tooltip>
                                </q-icon>
                                <q-icon v-else name="event" size="16px" color="grey-7"/>

                                <q-popup-proxy ref="datePopup" transition-show="scale" transition-hide="scale">
                                    <CommandCenterMonthRangePicker
                                        :model-value="selectedDateRange"
                                        :min-date="dateBounds.min_date"
                                        :max-date="dateBounds.max_date"
                                        @update:model-value="selectDateRange"
                                    />
                                </q-popup-proxy>
                            </button>
                        </div>
                    </template>
                    <div v-else class="flex items-center q-ml-lg" style="height: 58px;">
                        <q-spinner color="accent" size="20px" class="q-mr-md"/>
                        <div style="font-size: 14px; color: #5f5f5f;">Loading filters...</div>
                    </div>

                    <q-btn v-if="!loadingOptions" unelevated color="accent" class="q-px-lg q-ml-md" style="border-radius: 6px; height: 40px;" :loading="loadingDashboard" :disable="loadingOptions || !canSubmit" @click="fetchDashboardData">
                        <div style="font-size: 13px; font-weight: 600; letter-spacing: 1px;">Submit</div>
                    </q-btn>

                    <div v-if="!loadingOptions && availableObjectiveOptions.length" class="cc-selected-count q-ml-md">
                        {{ selectedObjectives.length }} of {{ availableObjectiveOptions.length }} BCOs selected
                    </div>

                    <q-space/>

                    <div v-if="dashboard" class="cc-insights q-ml-md">
                        <div class="cc-cycle-label">{{ periodHeading }}</div>

                        <q-btn unelevated no-caps color="accent" class="cc-insights-btn q-px-md" @click="isChatOpen = !isChatOpen">
                            <q-icon name="chat_bubble" size="16px" class="q-mr-sm"/>
                            <div style="font-size: 13px; font-weight: 600; letter-spacing: 0.5px;">NBA Copilot</div>
                            <q-tooltip>{{ isChatOpen ? 'Close Chat' : 'Ask about BCO performance' }}</q-tooltip>
                        </q-btn>
                    </div>
                </div>

                <!-- BCO TILES -->
                <div v-if="!loadingOptions" class="cc-bco">
                    <div class="cc-bco__header">
                        <div class="cc-bco__label">BCO / USE CASE</div>
                        <button v-if="availableObjectiveOptions.length" type="button" class="cc-bco__toggle-all" @click="toggleAllObjectives">
                            {{ allObjectivesSelected ? "Clear all" : "Select all" }}
                        </button>
                    </div>

                    <q-scroll-area v-if="availableObjectiveOptions.length" :thumb-style="thumbStyle" :horizontal-thumb-style="{ height: '5px' }" style="height: 94px;">
                        <div class="cc-bco__tiles">
                            <button
                                v-for="objective in availableObjectiveOptions"
                                :key="`bco-${objective.name}`"
                                type="button"
                                class="cc-bco__tile"
                                :class="{ 'cc-bco__tile--selected': isObjectiveSelected(objective.name) }"
                                :aria-pressed="isObjectiveSelected(objective.name)"
                                :title="objective.name"
                                @click="toggleObjective(objective.name)"
                            >
                                <div class="cc-bco__name">
                                    <span class="cc-bco__dot" :class="sourceModifier(objective.source, 'cc-bco__dot')"></span>
                                    <span class="ellipsis">{{ objective.name }}</span>
                                </div>
                                <div class="cc-bco__pill" :class="sourceModifier(objective.source, 'cc-bco__pill')">{{ formatSource(objective.source) }}</div>
                                <div class="cc-bco__imperative ellipsis" :title="objective.imperatives.join(', ')">{{ objective.imperatives.join(", ") }}</div>
                            </button>
                        </div>
                    </q-scroll-area>

                    <div v-else class="cc-bco__empty">Select a strategic imperative to see its BCOs</div>
                </div>
            </div>

            <!-- DASHBOARD -->
            <div style="padding: 16px;">
                <div v-if="loadingDashboard" class="flex full-width justify-center items-center q-mt-xl">
                    <q-spinner color="accent" size="20px" class="q-mr-md"/>
                    <div style="font-size: 12px; color: #5f5f5f;">Loading dashboard...</div>
                </div>

                <div v-else-if="!dashboard" class="flex column items-center text-center" style="padding-top: calc((100vh - 350px) / 2);">
                    <div style="color: #414141; font-size: 16px; font-weight: 600;">Choose your filters and click Submit</div>
                    <div v-if="selectedDateRange" class="q-mt-sm" style="color: #858585; font-size: 12px; line-height: 1.45;">
                        The command centre shows {{ formatMonthRange(selectedDateRange) }}<br>
                        for the brand, imperative and BCOs you pick.
                    </div>
                    <div v-else class="q-mt-sm" style="color: #858585; font-size: 12px; line-height: 1.45;">
                        The command centre compares {{ currentCycleLabel }} against the prior cycle<br>
                        for the brand, imperative and BCOs you pick.
                    </div>
                </div>

                <template v-else>
                    <!-- KPI CARDS -->
                    <div class="cc-kpi-grid" :class="{ 'cc-kpi-grid--compact': isChatOpen }">
                        <CommandCenterKpiCard
                            v-for="card in kpiCards"
                            :key="`kpi-${card.key}`"
                            :label="card.label"
                            :value="card.value"
                            :off-target="card.offTarget"
                            :delta="card.delta"
                            :show-delta="!hasCustomDateRange"
                            :target="card.target"
                            :info="card.info"
                        />
                    </div>

                    <div class="cc-panels q-mt-md" :class="{ 'cc-panels--compact': isCompactLayout }">
                        <div class="cc-row">
                            <!-- OBJECTIVE HEALTH card -->
                            <div class="cc-card cc-card--health">
                                <div class="cc-card__header">
                                    <div class="cc-card__title">OBJECTIVE HEALTH</div>
                                </div>

                                <table v-if="dashboard.objective_health.length" class="cc-table">
                                    <thead>
                                        <tr>
                                            <th class="text-left">Objective</th>
                                            <th class="text-left">Source</th>
                                            <th class="text-left">Status</th>
                                            <th class="text-right">Cohort HCPs</th>
                                            <th class="text-right">Recommendations</th>
                                            <th class="text-right">Engagement Rate</th>
                                            <th class="text-right">Accept Rate</th>
                                            <th class="text-right">
                                                Health
                                                <q-icon name="info_outline" size="13px" color="grey-6" class="cc-health-info">
                                                    <q-tooltip anchor="top middle" self="bottom middle" :offset="[0, 6]" max-width="240px" class="bg-grey-9" style="font-size: 10px;">This is based on Accept Rate: Good if above 80%, Average if between 50% and 80% inclusive, and At-Risk if below 50%.</q-tooltip>
                                                </q-icon>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="objective in objectiveHealthPage.rows" :key="`objective-${objective.objective_id}`">
                                            <td class="text-left text-bold">{{ objective.objective_name }}</td>
                                            <td class="text-left" :class="{ 'text-accent text-bold': objective.objective_source === 'Agent' }">{{ formatObjectiveSource(objective.objective_source) }}</td>
                                            <td class="text-left">{{ formatLabel(objective.objective_status) }}</td>
                                            <td class="text-right">{{ formatCount(objective.cohort_hcp_count) }}</td>
                                            <td class="text-right">{{ formatCount(objective.suggestions) }}</td>
                                            <td class="text-right">{{ formatPercent(objective.engagement_rate) }}</td>
                                            <td class="text-right">{{ formatPercent(objective.accept_rate) }}</td>
                                            <td class="text-right" :class="healthBandClass(objective.objective_health_band)">{{ formatHealthBand(objective.objective_health_band) }}</td>
                                        </tr>
                                    </tbody>
                                </table>

                                <div v-if="objectiveHealthPage.count > 1" class="cc-pagination">
                                    <div class="cc-pagination__range">{{ objectiveHealthPage.rangeLabel }}</div>
                                    <q-pagination v-model="objectiveHealthPageNumber" :max="objectiveHealthPage.count" :max-pages="5" boundary-numbers direction-links flat color="grey-7" active-color="grey-8" size="10px" gutter="2px"/>
                                </div>

                                <div v-if="!dashboard.objective_health.length" class="flex full-width justify-center q-py-lg" style="font-size: 12px; color: #5f5f5f;">No objectives match the selected filters</div>
                            </div>

                            <!-- TOP REASONS FOR DISMISSAL card -->
                            <div class="cc-card cc-card--dismiss">
                                <div class="cc-card__header">
                                    <div class="cc-card__title">TOP REASONS FOR DISMISSAL</div>
                                </div>

                                <CommandCenterDismissReasons v-if="dashboard.dismiss_reasons.length" :reasons="dashboard.dismiss_reasons"/>
                                <div v-else class="flex full-width justify-center q-py-lg" style="font-size: 12px; color: #5f5f5f;">No dismissals match the selected filters</div>
                            </div>
                        </div>

                        <div class="cc-row cc-row--thirds q-mt-md">
                            <!-- CHANNEL MIX card -->
                            <div class="cc-card cc-card--channel">
                                <div class="cc-card__header">
                                    <div class="cc-card__title">CHANNEL MIX</div>
                                </div>

                                <CommandCenterChannelMix v-if="dashboard.channel_mix.length" :channels="dashboard.channel_mix"/>
                                <div v-else class="flex full-width justify-center q-py-lg" style="font-size: 12px; color: #5f5f5f;">No suggestions match the selected filters</div>
                            </div>

                            <!-- TIER-WISE PERFORMANCE card -->
                            <div class="cc-card cc-card--tier">
                                <div class="cc-card__header">
                                    <div class="cc-card__title">TIER-WISE PERFORMANCE</div>
                                </div>

                                <CommandCenterTierPerformance v-if="dashboard.tier_performance.length" :tiers="dashboard.tier_performance"/>
                                <div v-else class="flex full-width justify-center q-py-lg" style="font-size: 12px; color: #5f5f5f;">No suggestions match the selected filters</div>
                            </div>

                            <!-- ACCEPT RATE TREND card -->
                            <div class="cc-card cc-card--trend">
                                <div class="cc-card__header">
                                    <div class="cc-card__title">ACCEPT RATE TREND</div>
                                    <div class="cc-card__subtitle">All cycles</div>
                                </div>

                                <CommandCenterAcceptRateTrend
                                    v-if="dashboard.accept_trend.cycles.length"
                                    :cycles="dashboard.accept_trend.cycles"
                                    :target="dashboard.accept_trend.target"
                                    :sources="dashboard.accept_trend.by_objective_source"
                                    :period-label="hasCustomDateRange ? periodLabel : ''"
                                />
                                <div v-else class="flex full-width justify-center q-py-lg" style="font-size: 12px; color: #5f5f5f;">No suggestions match the selected filters</div>
                            </div>
                        </div>

                        <div class="cc-row cc-row--full q-mt-md">
                            <!-- TERRITORY PERFORMANCE card -->
                            <div class="cc-card cc-card--territory">
                                <div class="cc-card__header">
                                    <div class="cc-card__title">TERRITORY PERFORMANCE</div>
                                </div>

                                <table v-if="dashboard.territory_performance.length" class="cc-table">
                                    <thead>
                                        <tr>
                                            <th class="text-left">Territory</th>
                                            <th class="text-left cc-territory__rep">Rep</th>
                                            <th class="text-center">Recommendations</th>
                                            <template v-for="rate in territoryRates" :key="`territory-head-${rate.key}`">
                                                <th class="text-center">{{ rate.label }}</th>
                                                <th v-if="!hasCustomDateRange" class="text-center cc-territory__change">{{ rate.label }} vs prior cycle</th>
                                            </template>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr
                                            v-for="territory in territoryPage.rows"
                                            :key="`territory-${territory.territory_name}-${territory.rep_name}`"
                                            :class="{ 'cc-table__row--declined': engagementDeclined(territory) }"
                                        >
                                            <td class="text-left text-bold">{{ territory.territory_name }}</td>
                                            <td class="text-left">{{ territory.rep_name }}</td>
                                            <td class="text-center">{{ formatCount(territory.suggestions) }}</td>
                                            <template v-for="rate in territoryRates" :key="`territory-cell-${rate.key}`">
                                                <td class="text-center">{{ formatPercent(territory[rate.key]) }}</td>
                                                <td v-if="!hasCustomDateRange" class="text-center text-bold">{{ formatRateChange(territory, rate.key) }}</td>
                                            </template>
                                        </tr>
                                    </tbody>
                                </table>

                                <div v-if="territoryPage.count > 1" class="cc-pagination">
                                    <div class="cc-pagination__range">{{ territoryPage.rangeLabel }}</div>
                                    <q-pagination v-model="territoryPageNumber" :max="territoryPage.count" :max-pages="5" boundary-numbers direction-links flat color="grey-7" active-color="grey-8" size="10px" gutter="2px"/>
                                </div>

                                <div v-if="!dashboard.territory_performance.length" class="flex full-width justify-center q-py-lg" style="font-size: 12px; color: #5f5f5f;">No territories match the selected filters</div>
                            </div>
                        </div>
                    </div>
                </template>
            </div>
        </q-scroll-area>
    </div>

        <!-- CHAT PANEL -->
        <div v-if="isChatOpen" :style="`width: ${chatPanelWidth}; height: calc(100vh - 60px); border-left: 1px solid #dedede;`">
            <div class="flex full-width justify-between items-start q-py-sm q-pl-md q-pr-sm" style="border-bottom: 1px solid #dedede;">
                <div class="column">
                    <div style="font-size: 12px; font-weight: 600;">Ask about BCO performance</div>
                    <div style="font-size: 10px; color: #858585;">KPIs · objectives · territories · channels</div>
                </div>
                <div class="self-center">
                    <q-btn flat no-caps dense color="grey-8" class="q-py-none q-px-xs q-mr-sm" style="border-radius: 6px; font-size: 12px;" @click="showOlderChats = true"><q-icon name="list_alt" size="16px" class="q-mr-xs" /><span>History</span><q-tooltip>View Older Chats</q-tooltip></q-btn>
                    <q-btn flat no-caps dense color="grey-8" class="q-py-none q-px-xs" style="border-radius: 6px; font-size: 12px;" @click="createNewChat"><q-icon name="add" size="16px" class="q-mr-xs" /><span>New</span><q-tooltip>New Chat</q-tooltip></q-btn>
                </div>
                <q-btn flat icon="close" size="xs" color="grey-8" class="q-py-none q-px-xs" style="border-radius: 6px;" @click="isChatOpen = false"><q-tooltip>Close</q-tooltip></q-btn>
            </div>

            <q-scroll-area class="full-width chat-scroll" :thumb-style="thumbStyle" :horizontal-offset="[0, 0]" :style="{ height: chatScrollHeight }" ref="chatScroll">
                <div v-if="!loadingOldChat" class="flex full-width">
                    <template v-if="qna.length">
                        <div v-for="(msg, msgIndex) in qna" :key="msgIndex" class="full-width flex ">
                            <!-- Question chat bubble -->
                            <div v-if="msg.question" class="flex full-width justify-end q-pr-sm q-mt-sm">
                                <!-- Editing: the bubble becomes an input with its own send button -->
                                <div v-if="editingQnaKey === msg.key" class="question-edit-box q-pa-sm">
                                    <textarea ref="editQuestionInput" v-model="editingQuestionText" class="question-edit-input" rows="2" @keydown.enter.exact.prevent="submitEditedQuestion(msg)" @keydown.esc.prevent="cancelEditQuestion"></textarea>
                                    <div class="flex justify-end items-center q-mt-xs">
                                        <q-btn outline rounded no-caps color="secondary" size="sm" class="q-py-none q-px-sm q-mr-sm bg-white" @click="cancelEditQuestion">Cancel</q-btn>
                                        <q-btn rounded unelevated no-caps color="accent" size="sm" class="q-py-none q-px-sm" @click="submitEditedQuestion(msg)" :disable="!editingQuestionText.trim() || isGenerating"><b>Send</b></q-btn>
                                    </div>
                                </div>

                                <div v-else class="question-block">
                                    <q-btn flat dense icon="edit" size="xs" color="grey-8" class="question-edit-btn q-mr-xs" :disable="isGenerating" @click="startEditQuestion(msg)"><q-tooltip>Edit question &amp; resend</q-tooltip></q-btn>
                                    <div class="question-bubble q-pa-sm bg-grey-2">{{msg.question}}</div>
                                </div>
                            </div>

                            <!-- Thought trace chat bubble -->
                            <div v-if="traceSteps(msg).length" class="flex full-width justify-start q-pl-sm q-mt-sm">
                                <div class="answer-bubble q-pa-sm">
                                    <ThoughtTrace :data="traceSteps(msg)" :loading="msg?.loading" :answered="answerChunks(msg).length > 0" @setscroll="setChatScroll"/>
                                    <q-spinner-dots v-if="msg.loading === true && !answerChunks(msg).length" size="24px" color="secondary" class="q-mx-sm"/>
                                </div>
                            </div>

                            <!-- Answer chat bubble -->
                            <div v-if="answerChunks(msg).length || (msg.loading === true && !traceSteps(msg).length)" class="flex full-width justify-start q-pl-sm q-mt-sm">
                                <div class="answer-bubble q-pa-sm">
                                    <AgentTrustScoreButton v-if="trustScoreChunk(msg) && msg.loading !== true" :scores="trustScoreChunk(msg).critic_scores || []" :error="trustScoreChunk(msg).critic_error || ''" class="answer-trust-score"/>
                                    <div v-for="(chunk, chunkIndex) in answerChunks(msg)" :key="chunkIndex">
                                        <!-- TEXT -->
                                        <template v-if="chunk.text">
                                            <div v-for="(text, textIndex) in chunk.text" :key="textIndex" v-html="markdownToHtml(text)" class="recommendation-markdown text-black"></div>
                                        </template>

                                        <!-- CHARTS -->
                                        <template v-if="chunk.charts">
                                            <div v-for="(chart, chartIndex) in chunk.charts" :key="chartIndex" class="chart-thumb-wrap q-mt-sm">
                                                <div class="chart-thumb" @click="expandChart(chart, chatAttachmentName(msg, 'chart', chartIndex))">
                                                    <PlotlyChart :chart-data="chart" size="small"/>
                                                    <q-icon name="zoom_out_map" size="14px" class="chart-thumb-icon"/>
                                                    <q-tooltip anchor="top middle" self="bottom middle" :offset="[0, 6]" class="bg-grey-9" style="font-size: 10px;">Click to expand the chart</q-tooltip>
                                                </div>
                                                <q-btn flat dense icon="download" size="xs" class="chart-download-btn" @click="downloadChart(chart, chatAttachmentName(msg, 'chart', chartIndex))">
                                                    <q-tooltip anchor="top middle" self="bottom middle" :offset="[0, 6]" class="bg-grey-9" style="font-size: 10px;">Download as image</q-tooltip>
                                                </q-btn>
                                            </div>
                                        </template>

                                        <!-- TABLE -->
                                        <div v-if="chunk.table" v-for="(table, tableIndex) in chunk.table" :key="tableIndex">
                                            <ChatTable :table="table" :download-file-name="chatAttachmentName(msg, 'table', tableIndex)" :style="`max-width: calc(100vw*${chatPanelWidthValue} - 40px);`"/>
                                        </div>

                                        <!-- ERROR MESSAGES -->
                                        <div v-if="chunk.is_error" class="flex items-center text-negative">{{ chunk.error_message }}</div>

                                        <!-- NEXT QUESTIONS -->
                                        <div v-if="chunk.next_questions?.length" class="q-mt-sm">
                                            <div style="font-size: 9px; font-weight: 700; letter-spacing: 1px; padding-bottom: 6px; border-top: 1px solid #ffd3c0; padding-top: 8px; color: #424242;">MORE TO EXPLORE</div>
                                            <div v-for="(question, questionIndex) in chunk.next_questions" :key="question" class="suggested-question" :style="`margin-bottom: ${questionIndex === chunk.next_questions.length - 1 ? '0' : '6px'};`" @click="askSuggestedQuestion(question)">{{ question }}</div>
                                        </div>
                                    </div>

                                    <!-- LOADING -->
                                    <template v-if="msg.loading === true">
                                        <q-spinner-dots size="24px" color="secondary" class="q-mx-sm"/>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
                <div v-else class="flex full-width justify-center items-center q-py-lg">
                    <q-spinner color="accent" size="20px" class="q-mr-md"/>
                    <div style="font-size: 12px; color: #5f5f5f;">Loading chat...</div>
                </div>
                <div style="height: 8px;"></div>
            </q-scroll-area>

            <!-- DEFAULT QUESTIONS -->
            <div v-if="defaultQuestions.length && qna.length === 0" ref="defaultQuestionsSection" class="full-width q-px-md q-py-sm" style="border-top: 1px solid #dedede; position: relative;">
                <q-resize-observer @resize="updateDefaultQuestionsHeight"/>
                <div style="color: #424242; font-size: 9px; font-weight: 700; letter-spacing: 1px; padding-bottom: 8px; border-bottom: 1px solid #eeeeee;">TRY ASKING</div>
                <div v-for="(question, index) in defaultQuestions" :key="question" class="suggested-question" :style="`margin-bottom: ${index === defaultQuestions.length - 1 ? '0' : '8px'};`" @click="askSuggestedQuestion(question)">{{ question }}</div>
            </div>

            <div class="flex full-width items-center q-py-sm q-pr-sm" style="border-top: 1px solid #dedede;">
                <div class="col q-mx-sm">
                    <TextInput v-model="text" placeholder="e.g. which BCO has the lowest accept rate?" style="width: calc(100% / 0.85); max-width: calc(100% / 0.85); transform: scale(0.85); transform-origin: left;" @keydown.enter.prevent="handleSend"/>
                </div>
                <q-btn unelevated color="accent" icon="send" size="sm" class="q-py-none q-px-sm" style="border-radius: 6px;" :disable="!text.trim() || isGenerating" @click="handleSend"/>
            </div>
        </div>

        <!-- EXPANDED CHART POPUP -->
        <q-dialog v-model="isChartExpanded" @hide="expandedChart = null">
            <q-card style="width: 70vw; max-width: 1100px; border-radius: 8px;">
                <div class="flex full-width justify-between items-center q-py-sm q-pl-md q-pr-sm" style="border-bottom: 1px solid #dedede;">
                    <div style="font-size: 12px; font-weight: 600;">Chart</div>
                    <div>
                        <q-btn flat icon="download" size="xs" color="grey-8" class="q-py-none q-px-xs q-mr-sm" style="border-radius: 6px;" @click="downloadChart(expandedChart, expandedChartFileName)"><q-tooltip>Download as image</q-tooltip></q-btn>
                        <q-btn flat icon="close" size="xs" color="grey-8" class="q-py-none q-px-xs" style="border-radius: 6px;" @click="expandedChart = null"/>
                    </div>
                </div>
                <div class="full-width q-pa-md">
                    <PlotlyChart v-if="expandedChart" :chart-data="expandedChart" size="large" />
                </div>
            </q-card>
        </q-dialog>

        <!-- OLD CHATS POPUP -->
        <q-dialog v-model="showOlderChats" @show="getChatList" @hide="showOlderChats = false; editingChatId = null;">
            <q-card style="width: 420px; max-width: 90vw; border-radius: 8px;">
                <div class="flex full-width justify-between items-center q-py-sm q-pl-md q-pr-sm" style="border-bottom: 1px solid #dedede;">
                    <div style="font-size: 12px; font-weight: 600;">Older Chats</div>
                    <q-btn flat icon="close" size="xs" color="grey-8" class="q-py-none q-px-xs" style="border-radius: 6px;" @click="showOlderChats = false"/>
                </div>

                <q-scroll-area class="q-pr-md q-pl-sm" :thumb-style="thumbStyle" :horizontal-offset="[0, 4]" style="height: min(65vh, 420px); width: 100%;">
                    <template v-if="loadingChatList">
                        <div class="flex full-width justify-center items-center q-py-lg">
                            <q-spinner color="accent" size="20px" class="q-mr-md"/>
                            <div style="font-size: 12px; color: #5f5f5f;">Loading chats...</div>
                        </div>
                    </template>

                    <template v-else-if="sortedChatList.length">
                        <div v-for="chat in sortedChatList" :key="`old-chat-${chat.chat_id}`" class="old-chat-row" :class="{ 'old-chat-row--selected': chat_id === chat.chat_id }" @click="handleOldChatClick(chat)">
                            <template v-if="editingChatId === chat.chat_id">
                                <input
                                    v-model="editingChatNameInput"
                                    class="old-chat-name-input"
                                    ref="renameInput"
                                    @click.stop
                                    @keydown.enter.prevent="confirmEditChatName(chat)"
                                    @keydown.esc.prevent="editingChatId = null"
                                />
                                <q-btn flat dense icon="check" size="xs" color="positive" class="q-ml-xs" @click.stop="confirmEditChatName(chat)"><q-tooltip>Save</q-tooltip></q-btn>
                                <q-btn flat dense icon="close" size="xs" color="grey-8" @click.stop="editingChatId = null"><q-tooltip>Cancel</q-tooltip></q-btn>
                            </template>
                            <template v-else>
                                <div class="old-chat-name">{{ chat.chat_name || "New Chat" }}</div>
                                <q-btn flat dense icon="edit" size="xs" color="grey-6" class="old-chat-action-btn q-mr-xs" @click.stop="startEditChatName(chat)"><q-tooltip>Rename chat</q-tooltip></q-btn>
                                <q-btn flat dense icon="push_pin" size="xs" :color="chat.pinned ? 'accent' : 'grey-6'" class="old-chat-action-btn q-mr-xs" :class="{ 'is-visible': chat.pinned }" @click.stop="pinchat(chat)"><q-tooltip>{{ chat.pinned ? 'Unpin chat' : 'Pin chat' }}</q-tooltip></q-btn>
                                <q-btn flat dense icon="delete" size="xs" color="grey-6" class="old-chat-action-btn" @click.stop="confirmDeleteOldChat(chat)"><q-tooltip>Delete chat</q-tooltip></q-btn>
                            </template>
                        </div>
                    </template>

                    <template v-else>
                        <div class="flex full-width justify-center items-center q-py-lg">
                            <div style="font-size: 12px; color: #5f5f5f;">No older chats yet</div>
                        </div>
                    </template>
                </q-scroll-area>
            </q-card>
        </q-dialog>
    </div>
</template>

<script>
import APIService from '../../APIService.js';
import SingleSelect from '@/components/SingleSelect.vue';
import MultiSelect from '@/components/MultiSelect.vue';
import CommandCenterKpiCard from '@/components/CommandCenterKpiCard.vue';
import CommandCenterChannelMix from '@/components/CommandCenterChannelMix.vue';
import CommandCenterTierPerformance from '@/components/CommandCenterTierPerformance.vue';
import CommandCenterDismissReasons from '@/components/CommandCenterDismissReasons.vue';
import CommandCenterAcceptRateTrend from '@/components/CommandCenterAcceptRateTrend.vue';
import CommandCenterMonthRangePicker from '@/components/CommandCenterMonthRangePicker.vue';
import TextInput from '@/components/TextInput.vue';
import ChatTable from '@/components/ChatTable.vue';
import PlotlyChart from '@/components/PlotlyChartsComponent.vue';
import { chatAttachmentFileName, downloadChartImage } from '@/chatDownloads.js';
import ThoughtTrace from '@/components/ThoughtTrace.vue';
import AgentTrustScoreButton from '@/components/AgentTrustScoreButton.vue';
import { isTrustScoreChunk } from '@/agentTrustScores.js';
import { authenticatedFetch } from "@/auth.js"

const ALL_OPTION = "All";

// Below this width the dashboard next to an open chat panel switches to the stacked layout.
const COMPACT_LAYOUT_MAX_WIDTH = 1200;

// Dismiss and ignore rates are on target when they stay below it, the rest when they reach it.
const KPI_CARDS = [
    { key: "suggestions", label: "NBA Recommendations", isCount: true },
    { key: "reach_rate", label: "HCP Reach", info: "Percentage of total target HCPs who received recommendation during the selected period." },
    {
        key: "engagement_rate",
        label: "Engagement Rate",
        info: "Percentage of decisions for which the rep responded by either accepting or dismissing the recommendation.",
    },
    { key: "accept_rate", label: "Accept Rate", info: "Percentage of total decisions accepted by reps." },
    { key: "dismiss_rate", label: "Dismiss Rate", lowerIsBetter: true, info: "Percentage of total decisions actively dismissed by reps." },
    { key: "ignore_rate", label: "Ignore Rate", lowerIsBetter: true, info: "Percentage of total decisions that received no response from reps." },
    { key: "action_rate", label: "Action Rate", info: "Percentage of total decisions accepted and subsequently actioned by reps." },
];

// Each rate is shown next to its change against the prior cycle, read from `prior_<key>`.
const TERRITORY_RATES = [
    { key: "engagement_rate", label: "Engagement Rate" },
    { key: "accept_rate", label: "Accept Rate" },
    { key: "dismiss_rate", label: "Dismiss Rate" },
    { key: "ignore_rate", label: "Ignore Rate" },
];

const HEALTH_BAND_LABELS = { GOOD: "Good", AVERAGE: "Average", AT_RISK: "At-Risk" };

const TABLE_ROWS_PER_PAGE = 4;

const isMissing = (value) => value === null || value === undefined;

function paginate(rows, page) {
    const start = (page - 1) * TABLE_ROWS_PER_PAGE;
    const pageRows = rows.slice(start, start + TABLE_ROWS_PER_PAGE);
    return {
        rows: pageRows,
        count: Math.ceil(rows.length / TABLE_ROWS_PER_PAGE),
        rangeLabel: `${start + 1}-${start + pageRows.length} of ${rows.length}`,
    };
}

const toPercent = (rate) => (isMissing(rate) ? null : Math.round(Number(rate) * 100));

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// "2026-08-01" reads as "Aug 2026". The parts are read from the string itself, since a Date
// would be built at UTC midnight and could show the previous month in the user's time zone.
function formatMonth(value) {
    const [year, month] = String(value).split("-").map(Number);
    return `${MONTH_NAMES[month - 1]} ${year}`;
}

// { start_date, end_date } reads as "Aug 2026 – Sep 2026", or as one month when both are the same.
function formatMonthRange(range) {
    if (!range) return "";
    const start = formatMonth(range.start_date);
    const end = formatMonth(range.end_date);
    return start === end ? start : `${start} – ${end}`;
}

// Percentages are compared after rounding so the change always matches the two values on screen.
function describeChange(current, previous, unit) {
    if (isMissing(current) || isMissing(previous)) {
        return null;
    }

    const change = current - previous;
    if (change === 0) {
        return { direction: "flat", text: "No change" };
    }
    return { direction: change > 0 ? "up" : "down", text: `${Math.abs(change)}${unit}` };
}

export default {
    name: 'CommandCenterView',

    components: {
        SingleSelect,
        MultiSelect,
        CommandCenterKpiCard,
        CommandCenterChannelMix,
        CommandCenterTierPerformance,
        CommandCenterDismissReasons,
        CommandCenterAcceptRateTrend,
        CommandCenterMonthRangePicker,
        TextInput,
        ChatTable,
        PlotlyChart,
        ThoughtTrace,
        AgentTrustScoreButton,
    },

    data() {
        return {
            // The view is kept alive while the user is on another screen, so it has to know
            // when its DOM is detached: every height reads as 0 until it is shown again.
            isViewActive: true,

            isChatOpen: false,
            dashboardWidth: 0,
            text: "",
            defaultQuestions: [],
            defaultQuestionsHeight: 0,
            scrollHeight: 0,
            qna: [],
            // Covers the whole request, so switching chats mid-stream can't re-enable sending.
            isAwaitingAnswer: false,
            chat_id: null,
            chatList: [],
            expandedChart: null,
            expandedChartFileName: "",
            chat_name: "New Chat",
            editedChatName: "New Chat",
            showOlderChats: false,
            loadingOldChat: false,
            loadingChatList: false,
            editingChatId: null,
            editingChatNameInput: "",
            editingQnaKey: null,
            editingQuestionText: "",

            loadingOptions: false,
            brandOptions: [],
            strategicImperativeOptions: [],
            // [{ name, source }], one per BCO.
            objectiveOptions: [],
            // { imperative: [bco, ...] }, which the BCO tiles are narrowed by.
            strategicImperativeObjectives: {},

            selectedBrand: "",
            selectedStrategicImperatives: [],
            selectedObjectives: [],
            // { start_date, end_date } as the first days of the first and last months (YYYY-MM-01),
            // or null to show the current cycle.
            selectedDateRange: null,
            // The first and last dates in the data, as YYYY-MM-DD, which the month picker stays within.
            dateBounds: { min_date: null, max_date: null },

            loadingDashboard: false,
            dashboard: null,
            territoryRates: TERRITORY_RATES,
            objectiveHealthPageNumber: 1,
            territoryPageNumber: 1,

            thumbStyle: {
                borderRadius: '7px',
                backgroundColor: '#E5E8ED',
                width: '7px',
                opacity: 0.99
            },
            scrollContentStyle: { width: '100%' },
        };
    },

    async created() {
        this.fetchDefaultQuestions();
        this.loadingOptions = true;
        try {
            const response = await APIService.post(`${process.env.VUE_APP_ROOT_API}/common/command-center-options`, {});
            this.brandOptions = response.data.brandOptions || [];
            this.strategicImperativeOptions = response.data.strategicImperativeOptions || [];
            this.objectiveOptions = response.data.objectiveOptions || [];
            this.strategicImperativeObjectives = response.data.mappings?.strategicImperativeObjectives || {};
            this.dateBounds = response.data.dateBounds || { min_date: null, max_date: null };
            this.selectedBrand = this.brandOptions[0] || "";
            this.selectStrategicImperatives([...this.strategicImperativeOptions]);
        } catch (error) {
            console.error(error);
            this.$q.notify({
                message: "Error loading filters",
                color: "negative",
                icon: "error",
                position: "top-right",
                timeout: 3000,
            });
        } finally {
            this.loadingOptions = false;
        }
    },

    // Chat answers started here keep running while the user is on another screen;
    // these hooks only re-measure what a detached DOM could not.
    activated() {
        this.isViewActive = true;
        this.updateDefaultQuestionsHeight();
        this.$nextTick(() => {
            if (this.isChatOpen) {
                this.setScrollHeight();
                this.animateScroll(0);
            }
        });
    },

    deactivated() {
        this.isViewActive = false;
        // Both dialogs render through a portal on <body>, which would otherwise stay on
        // screen over the next route when navigation happens outside of them.
        this.expandedChart = null;
        this.showOlderChats = false;
    },

    computed: {
        chatPanelWidth() {
            return this.isChatOpen ? '35%' : '0px';
        },
        chatPanelWidthValue() {
            return this.isChatOpen ? '0.35' : '0';
        },
        isCompactLayout() {
            return this.isChatOpen && this.dashboardWidth < COMPACT_LAYOUT_MAX_WIDTH;
        },
        chatScrollHeight() {
            // 167px = chat panel header + input row; the suggestions block is measured at runtime
            const suggestionsHeight = this.qna.length === 0 ? this.defaultQuestionsHeight : 0;
            return `calc(100vh - ${167 + suggestionsHeight}px)`;
        },
        sortedChatList() {
            // The API already returns pinned chats first; re-sorting keeps that order after a local pin toggle.
            return [...this.chatList].sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));
        },
        isGenerating() {
            return this.isAwaitingAnswer || this.qna.some(msg => msg.loading === true);
        },
        isChartExpanded: {
            get() {
                return Boolean(this.expandedChart);
            },
            set(value) {
                if (!value) {
                    this.expandedChart = null;
                }
            },
        },

        // A filter with nothing selected would match no rows, so the dashboard is not asked for.
        canSubmit() {
            return Boolean(this.selectedBrand)
                && this.selectedStrategicImperatives.length > 0
                && this.selectedObjectives.length > 0;
        },

        // The BCOs that belong to at least one selected imperative.
        // Each BCO comes with the selected imperatives it belongs to.
        availableObjectiveOptions() {
            const map = this.strategicImperativeObjectives;
            return this.objectiveOptions
                .map((objective) => ({
                    ...objective,
                    imperatives: this.selectedStrategicImperatives.filter((imperative) => map[imperative]?.includes(objective.name)),
                }))
                .filter((objective) => objective.imperatives.length > 0);
        },

        availableObjectiveNames() {
            return this.availableObjectiveOptions.map((objective) => objective.name);
        },

        allObjectivesSelected() {
            return this.availableObjectiveNames.length > 0 && this.selectedObjectives.length === this.availableObjectiveNames.length;
        },

        currentCycleLabel() {
            return this.dashboard?.current_cycle || "the current cycle";
        },

        // The date range the dashboard on screen was built for, or null when it shows the current cycle.
        // It is what was submitted, not what the picker holds now.
        appliedDateRange() {
            return this.dashboard?.date_range || null;
        },

        hasCustomDateRange() {
            return Boolean(this.appliedDateRange);
        },

        // The period every figure on the dashboard covers: the applied months, or the current cycle.
        periodLabel() {
            return this.hasCustomDateRange ? formatMonthRange(this.appliedDateRange) : this.currentCycleLabel;
        },

        periodHeading() {
            return this.hasCustomDateRange ? `Selected period · ${this.periodLabel}` : `This cycle · ${this.dashboard?.current_cycle}`;
        },

        objectiveHealthPage() {
            return paginate(this.dashboard?.objective_health || [], this.objectiveHealthPageNumber);
        },

        territoryPage() {
            return paginate(this.dashboard?.territory_performance || [], this.territoryPageNumber);
        },

        kpiCards() {
            const kpis = this.dashboard?.kpis || {};

            return KPI_CARDS.map((card) => {
                const { current, previous, target } = kpis[card.key] || {};

                if (card.isCount) {
                    return {
                        key: card.key,
                        label: card.label,
                        value: isMissing(current) ? "—" : this.formatCount(current),
                        offTarget: false,
                        delta: describeChange(current, previous, ""),
                    };
                }

                const currentPercent = toPercent(current);
                const targetPercent = toPercent(target);
                const missesTarget = !isMissing(currentPercent) && !isMissing(targetPercent)
                    && (card.lowerIsBetter ? currentPercent > targetPercent : currentPercent < targetPercent);

                return {
                    key: card.key,
                    label: card.label,
                    value: isMissing(currentPercent) ? "—" : `${currentPercent}%`,
                    offTarget: missesTarget,
                    delta: describeChange(currentPercent, toPercent(previous), "pp"),
                    target: isMissing(targetPercent) ? "" : `Target ${card.lowerIsBetter ? "< " : ""}${targetPercent}%`,
                    info: card.info,
                };
            });
        },
    },

    methods: {
        async fetchDashboardData() {
            this.loadingDashboard = true;
            try {
                const payload = {
                    brand: this.selectedBrand,
                    strategic_imperative: this.toFilterValue(this.selectedStrategicImperatives, this.strategicImperativeOptions),
                    objective: [...this.selectedObjectives],
                    ...(this.selectedDateRange || {}),
                };
                const response = await APIService.post(`${process.env.VUE_APP_ROOT_API}/common/fetch-dashboard-data`, payload);
                this.dashboard = response.data;
                this.objectiveHealthPageNumber = 1;
                this.territoryPageNumber = 1;
            } catch (error) {
                console.error(error);
                this.$q.notify({
                    message: "Error loading the dashboard",
                    color: "negative",
                    icon: "error",
                    position: "top-right",
                    timeout: 3000,
                });
            } finally {
                this.loadingDashboard = false;
            }
        },

        // The range runs from the first day of its first month to the first day of its last month,
        // the only day of a month the DATE column holds.
        selectDateRange(range) {
            this.selectedDateRange = range;
            this.$refs.datePopup?.hide();
        },

        formatMonthRange(range) {
            return formatMonthRange(range);
        },

        // The agent answers for the period the dashboard on screen shows.
        chatDateRange() {
            return {
                start_date: this.appliedDateRange?.start_date ?? null,
                end_date: this.appliedDateRange?.end_date ?? null,
            };
        },

        // Changing the imperatives resets the BCO tiles to all of the newly available BCOs.
        selectStrategicImperatives(selected) {
            this.selectedStrategicImperatives = selected;
            this.selectedObjectives = [...this.availableObjectiveNames];
        },

        isObjectiveSelected(name) {
            return this.selectedObjectives.includes(name);
        },

        // The selection is kept in the order of the tiles, whatever order they were picked in.
        toggleObjective(name) {
            const selected = new Set(this.selectedObjectives);
            if (selected.has(name)) {
                selected.delete(name);
            } else {
                selected.add(name);
            }
            this.selectedObjectives = this.availableObjectiveNames.filter((item) => selected.has(item));
        },

        toggleAllObjectives() {
            this.selectedObjectives = this.allObjectivesSelected ? [] : [...this.availableObjectiveNames];
        },

        formatSource(source) {
            return source || "Unknown";
        },

        // "approved" reads as "Approved"; "agent-recommended" reads as "Agent-Recommended".
        formatObjectiveSource(source) {
            return String(source ?? "").toLowerCase().replace(/(^|[\s_-])(\w)/g, (_, separator, letter) => `${separator}${letter.toUpperCase()}`);
        },

        // "Agent" and "Existing" get their own colours, anything else the neutral one.
        sourceModifier(source, block) {
            const normalized = String(source || "").toLowerCase();
            return normalized === "agent" || normalized === "existing" ? `${block}--${normalized}` : "";
        },

        // A filter with every available option selected is sent as "All". The BCOs are always sent
        // as a list, since the tiles leave out the BCOs that have not completed a run.
        toFilterValue(selected, options) {
            return options.length > 0 && selected.length === options.length ? ALL_OPTION : [...selected];
        },

        // "AT_RISK" reads as "At Risk".
        formatLabel(value) {
            return String(value || "")
                .toLowerCase()
                .split("_")
                .filter(Boolean)
                .map((word) => word[0].toUpperCase() + word.slice(1))
                .join(" ");
        },

        formatCount(value) {
            return isMissing(value) ? "—" : Number(value).toLocaleString("en-US");
        },

        formatPercent(rate) {
            const percent = toPercent(rate);
            return isMissing(percent) ? "—" : `${percent}%`;
        },

        // Compared after rounding so a highlighted row always shows a lower engagement than its prior.
        rateChange(territory, key) {
            return describeChange(toPercent(territory[key]), toPercent(territory[`prior_${key}`]), "pp");
        },

        engagementDeclined(territory) {
            return this.rateChange(territory, "engagement_rate")?.direction === "down";
        },

        // The triangle carries the direction, so the change is shown without a sign.
        formatRateChange(territory, key) {
            const change = this.rateChange(territory, key);
            if (!change) return "—";
            if (change.direction === "flat") return "0pp";
            return `${change.direction === "up" ? "▲" : "▼"} ${change.text}`;
        },

        formatHealthBand(band) {
            return HEALTH_BAND_LABELS[String(band || "").toUpperCase()] ?? this.formatLabel(band);
        },

        healthBandClass(band) {
            const normalized = String(band || "").toUpperCase();
            if (normalized === "AT_RISK") return "text-accent text-bold";
            if (normalized === "GOOD") return "text-bold";
            return "";
        },

        async fetchDefaultQuestions() {
            try {
                const response = await APIService.get(`${process.env.VUE_APP_ROOT_API}/command-center-interaction-agent/default-questions`);
                this.defaultQuestions = response.data.defaultQuestions || [];
            } catch (error) {
                console.error(error);
                this.defaultQuestions = [];
            }
        },

        // A detached (kept-alive) view reports a width of 0, which is not a real layout width.
        updateDashboardWidth({ width }) {
            if (width > 0) {
                this.dashboardWidth = width;
            }
        },

        updateDefaultQuestionsHeight() {
            this.$nextTick(() => {
                const el = this.$refs.defaultQuestionsSection;
                // offsetHeight instead of the observer payload so the top border is included
                this.defaultQuestionsHeight = el ? el.offsetHeight : 0;
            });
        },

        markdownToHtml(markdown) {
            if (!markdown) {
                return "";
            }

            const escapeHtml = (text) => text
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/"/g, "&quot;")
                .replace(/'/g, "&#039;");
            const renderInline = (text) => escapeHtml(text).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
            const lines = markdown.split(/\r?\n/);
            const html = [];
            let listOpen = false;

            lines.forEach((line) => {
                const bullet = line.match(/^\s*[-*]\s+(.+)$/);

                if (bullet) {
                    if (!listOpen) {
                        html.push("<ul>");
                        listOpen = true;
                    }
                    html.push(`<li>${renderInline(bullet[1])}</li>`);
                    return;
                }

                if (listOpen) {
                    html.push("</ul>");
                    listOpen = false;
                }

                if (line.trim()) {
                    html.push(`<p>${renderInline(line.trim())}</p>`);
                }
            });

            if (listOpen) {
                html.push("</ul>");
            }

            return html.join("");
        },

        /* Chat Interface methods */

        chatAttachmentName(msg, kind, index) {
            return chatAttachmentFileName(this.editedChatName, msg?.key, kind, index + 1);
        },

        expandChart(chart, fileName) {
            this.expandedChart = chart;
            this.expandedChartFileName = fileName;
        },

        async downloadChart(chart, fileName) {
            try {
                await downloadChartImage(chart, fileName);
            } catch(err) {
                console.error("Error downloading chart:", err);
                this.$q.notify({
                    type: 'negative',
                    message: 'Failed to download the chart. Please try again.',
                    position: 'top-right',
                    timeout: 3000,
                });
            }
        },

        createNewChat() {
            try {
                this.text = "";
                this.cancelEditQuestion();
                this.qna = [];
                this.chat_id = null;
                this.chat_name = "New Chat";
                this.editedChatName = "New Chat";
            } catch(err) {
                console.log("Error creating new chat:", err);
            }
        },

        async fetchOldChat(chat) {
            try {
                this.loadingOldChat = true;
                this.cancelEditQuestion();
                this.editedChatName = chat.chat_name;
                this.chat_id = chat.chat_id;
                const send = { chat_id: chat.chat_id }
                const t = await APIService.post(`${process.env.VUE_APP_ROOT_API}/command-center-interaction-agent/get-old-chat`, send);
                this.qna = t.data.qna || [];
            } catch(err) {
                console.error(err);
            } finally {
                this.loadingOldChat = false;
            }
        },

        async pinchat(chat) {
            const wasPinned = Boolean(chat.pinned);
            chat.pinned = wasPinned ? 0 : 1;
            try {
                const send = { chat_id: chat.chat_id }
                await APIService.post(`${process.env.VUE_APP_ROOT_API}/command-center-interaction-agent/pin-chat`, send);
            } catch(err) {
                chat.pinned = wasPinned ? 1 : 0;
                this.$q.notify({
                    type: 'negative',
                    message: `Failed to ${wasPinned ? 'unpin' : 'pin'} chat. Please try again.`,
                    position: 'top-right',
                    timeout: 3000,
                });
            }
        },

        async editChatName(chat_id, chat_name) {
            try {
                let send = {
                    chat_id: chat_id,
                    chat_name: chat_name,
                }
                this.editedChatName = chat_name;
                await APIService.post(`${process.env.VUE_APP_ROOT_API}/command-center-interaction-agent/edit-chat-name`, send);
                this.chatList.find(x => x.chat_id == chat_id).chat_name = chat_name;
            } catch(err) {
                this.$q.notify({
                    type: 'negative',
                    message: 'Failed to rename chat. Please try again.',
                    position: 'top-right',
                    timeout: 3000,
                });
            }
        },

        async handleDeleteChat() {
            try {
                const send = { chat_id: this.chat_id }
                const t = await APIService.post(`${process.env.VUE_APP_ROOT_API}/command-center-interaction-agent/delete-chat`, send);
                if(t.status == 200){
                    await this.getChatList();
                }
                this.createNewChat();
            } catch(err) {
                this.$q.notify({
                    type: 'negative',
                    message: 'Failed to delete chat. Please try again.',
                    position: 'top-right',
                    timeout: 3000,
                });
            }
        },

        handleOldChatClick(chat) {
            if (this.editingChatId === chat.chat_id) {
                return;
            }
            this.showOlderChats = false;
            this.fetchOldChat(chat);
        },

        startEditChatName(chat) {
            this.editingChatId = chat.chat_id;
            this.editingChatNameInput = chat.chat_name;
            this.$nextTick(() => {
                const input = this.$refs.renameInput;
                const el = Array.isArray(input) ? input[0] : input;
                el?.focus();
            });
        },

        confirmEditChatName(chat) {
            const newName = this.editingChatNameInput.trim();
            this.editingChatId = null;
            if (!newName || newName === chat.chat_name) {
                return;
            }
            this.editChatName(chat.chat_id, newName);
        },

        confirmDeleteOldChat(chat) {
            if (!window.confirm(`Delete "${chat.chat_name || "New Chat"}"? This cannot be undone.`)) {
                return;
            }
            this.chat_id = chat.chat_id;
            this.handleDeleteChat();
        },

        async createNewChatInDB() {
            try{
                let t = await APIService.post(`${process.env.VUE_APP_ROOT_API}/command-center-interaction-agent/create-new-chat-in-db`, {});
                return t.data.chat_id;
            } catch(err) {
                console.error(err);
            }
        },

        // Shared by the starter chips and the follow-up chips on an answer.
        askSuggestedQuestion(question) {
            if (this.isGenerating) {
                return;
            }
            this.text = question;
            this.handleSend();
        },

        // The question is read and the input cleared before the first await: a second
        // Enter or click during chat creation would otherwise send a stale or empty question.
        async handleSend() {
            if (this.isGenerating) {
                return;
            }
            const question = this.text.trim();
            if (!question) {
                return;
            }
            this.text = "";
            this.isAwaitingAnswer = true;
            let temp_chat_id = null;
            let currentQnaIndex = -1;
            try {
                this.qna.push({
                    loading: true,
                    key: this.qna.length + 1,
                    question: question,
                    feedbacks: [],
                    timestamp: new Date(),
                    answer: [],
                });

                if(this.qna.length == 1){
                    this.chat_id = await this.createNewChatInDB();
                    if (!this.chat_id) {
                        this.qna.pop();
                        this.text = question;
                        return;
                    }
                }
                temp_chat_id = this.chat_id;
                currentQnaIndex = this.qna.length - 1;

                const send = {
                    question: question,
                    chat_id: this.chat_id,
                    regenerate: false,
                    selected_bcos: [...this.selectedObjectives],
                    ...this.chatDateRange(),
                    userObj: {
                        emailId: localStorage.getItem("emailId")
                    },
                    url: this.$route.fullPath.split("?")[0]
                };

                await new Promise(r => setTimeout(r, 10));
                this.setChatScroll();
                this.getAndSetChatName(this.chat_id, question);

                const response = await authenticatedFetch(`${process.env.VUE_APP_ROOT_API}/command-center-interaction-agent/send-question`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(send),
                })

                await this.streamAnswerIntoQna(response, currentQnaIndex, temp_chat_id);

                if(temp_chat_id === this.chat_id && this.qna.length > currentQnaIndex) {
                    await new Promise(r => setTimeout(r, 100));
                    delete this.qna[currentQnaIndex].loading;
                }
                await new Promise(r => setTimeout(r, 10));
                this.setChatScroll();
            } catch(err) {
                console.error(err);
                // An aborted stream rejects reader.read() rather than reporting done,
                // so the loader has to be cleared here or it spins forever.
                if (currentQnaIndex !== -1 && temp_chat_id === this.chat_id && this.qna.length > currentQnaIndex) {
                    if (!this.qna[currentQnaIndex].answer.some(chunk => chunk.is_error)) {
                        this.qna[currentQnaIndex].answer.push({
                            is_error: true,
                            error_message: "The connection was interrupted before the answer finished. Please try again.",
                        });
                    }
                    delete this.qna[currentQnaIndex].loading;
                }
            } finally {
                this.isAwaitingAnswer = false;
            }
        },

        // Reads the SSE body into qna[qnaIndex].answer. Chunks are dropped once the user
        // has moved to another chat or the entry is gone, so a slow stream can't write
        // into whatever now sits at that index.
        async streamAnswerIntoQna(response, qnaIndex, temp_chat_id) {
            const isStillCurrent = () => temp_chat_id === this.chat_id && this.qna.length > qnaIndex;

            if (!response.ok) {
                if (isStillCurrent()) {
                    this.qna[qnaIndex].answer.push({ is_error: true, error_message: `Request failed with status ${response.status}` });
                }
                return;
            }

            const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
            let buffer = '';

            while(true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += value;
                let lastIndex = buffer.lastIndexOf('\n\n');
                if (lastIndex !== -1) {
                    const completeMessages = buffer.substring(0, lastIndex);
                    buffer = buffer.substring(lastIndex + 2);
                    const messages = completeMessages.split('\n\n');
                    for (const message of messages) {
                        if (!message.trim()) continue;
                        const dataLines = message.split('\n').filter(line => line.startsWith('data:'));
                        if (dataLines.length === 0) continue;
                        const rawData = dataLines.map(line => line.replace(/^data:\s*/, '').trim()).join('');
                        try {
                            if (rawData) {
                                const chunk = JSON.parse(rawData);
                                if (isStillCurrent()) {
                                    this.qna[qnaIndex].answer.push(chunk);
                                }
                            }
                        } catch (e) {
                            console.error("Error parsing response chunk:", e, "Raw data that failed to parse:", rawData);
                            if (isStillCurrent()) {
                                this.qna[qnaIndex].answer.push({ is_error: true, error_message: "Something went wrong while generating the answer. Please try again." });
                            }
                        }
                    }
                }
            }
        },

        startEditQuestion(msg) {
            if (this.isGenerating) {
                return;
            }
            this.editingQnaKey = msg.key;
            this.editingQuestionText = msg.question;
            this.$nextTick(() => {
                const input = this.$refs.editQuestionInput;
                const el = Array.isArray(input) ? input[0] : input;
                if (!el) {
                    return;
                }
                el.focus();
                el.setSelectionRange(el.value.length, el.value.length);
            });
        },

        cancelEditQuestion() {
            this.editingQnaKey = null;
            this.editingQuestionText = "";
        },

        submitEditedQuestion(msg) {
            const question = this.editingQuestionText.trim();
            if (!question || this.isGenerating) {
                return;
            }
            this.cancelEditQuestion();
            this.regenerateResponse(msg.key, question);
        },

        // Re-answers an existing QNA row in place. The backend overwrites the stored
        // question, so the edited text replaces the original instead of appending a turn.
        async regenerateResponse(key, question) {
            const qnaIndex = this.qna.findIndex(msg => msg.key === key);
            if (qnaIndex === -1) {
                return;
            }
            const temp_chat_id = this.chat_id;
            this.isAwaitingAnswer = true;
            try {
                this.qna[qnaIndex] = {
                    loading: true,
                    key: key,
                    question: question,
                    feedbacks: [],
                    timestamp: new Date(),
                    answer: [],
                };

                const send = {
                    question: question,
                    chat_id: this.chat_id,
                    key: key,
                    regenerate: true,
                    selected_bcos: [...this.selectedObjectives],
                    ...this.chatDateRange(),
                };

                await new Promise(r => setTimeout(r, 10));
                this.setChatScroll();
                if(key === this.qna.length) {
                    this.getAndSetChatName(this.chat_id, question);
                }

                const response = await authenticatedFetch(`${process.env.VUE_APP_ROOT_API}/command-center-interaction-agent/regenerate-response`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(send),
                })

                await this.streamAnswerIntoQna(response, qnaIndex, temp_chat_id);

                if(temp_chat_id === this.chat_id && this.qna.length > qnaIndex) {
                    await new Promise(r => setTimeout(r, 100));
                    delete this.qna[qnaIndex].loading;
                }
                await new Promise(r => setTimeout(r, 10));
                this.setChatScroll();
            } catch(err) {
                console.error(err);
                // An aborted stream rejects reader.read() rather than reporting done,
                // so the loader has to be cleared here or it spins forever.
                if (temp_chat_id === this.chat_id && this.qna.length > qnaIndex) {
                    if (!this.qna[qnaIndex].answer.some(chunk => chunk.is_error)) {
                        this.qna[qnaIndex].answer.push({
                            is_error: true,
                            error_message: "The connection was interrupted before the answer finished. Please try again.",
                        });
                    }
                    delete this.qna[qnaIndex].loading;
                }
            } finally {
                this.isAwaitingAnswer = false;
            }
        },

        async getAndSetChatName(chat_id, question) {
            const send = {
                chat_id: chat_id,
                question: question,
            }
            const index = this.chatList.findIndex(x => x.chat_id == chat_id);
            let oldChatName;
            if(index >= 0) {
                oldChatName = this.chatList[index].chat_name;
            } else {
                oldChatName = "New chat";
            }
            try {
                const [t, _] = await Promise.all([APIService.post(`${process.env.VUE_APP_ROOT_API}/command-center-interaction-agent/get-chat-name`, send), this.getChatList()]);
                const newChatName = t.data.chat_name;
                const newIndex = this.chatList.findIndex(x => x.chat_id == chat_id);
                this.chatList[newIndex].chat_name = "";
                for(let i=0; i<newChatName.length; i++) {
                    this.chatList[newIndex].chat_name += newChatName[i];
                    await new Promise(r => setTimeout(r, 50));
                }
                this.editedChatName = newChatName;
            } catch(err) {
                if (index >= 0) {
                    this.chatList[index].chat_name = oldChatName;
                }
            }
        },

        animateScroll(duration = 600) {
            this.$refs.chatScroll?.setScrollPosition('vertical', this.scrollHeight, duration);
        },

        setScrollHeight() {
            const scrollArea = this.$refs.chatScroll;
            if (scrollArea) {
                this.scrollHeight = scrollArea.getScrollTarget().scrollHeight;
            }
        },

        traceSteps(msg) {
            return Array.isArray(msg?.answer) ? msg.answer.filter(chunk => chunk?.step_name) : [];
        },

        answerChunks(msg) {
            return Array.isArray(msg?.answer) ? msg.answer.filter(chunk => !chunk?.step_name && !isTrustScoreChunk(chunk)) : [];
        },

        trustScoreChunk(msg) {
            return Array.isArray(msg?.answer) ? msg.answer.find(isTrustScoreChunk) : undefined;
        },

        // An answer can stream in while the user is on another screen. The panel is detached
        // then, so its scroll height is 0 and scrolling is deferred to the next activation.
        setChatScroll() {
            if (!this.isViewActive || !this.isChatOpen) {
                return;
            }
            this.setScrollHeight();
            this.animateScroll();
        },

        async getCountChats() {
            try{
                let t = await APIService.post(`${process.env.VUE_APP_ROOT_API}/command-center-interaction-agent/get-count-chats`, {});
                return t.data;
            } catch(err) {
                this.chatList = [];
                console.error(err);
            }
        },

        async getChatList() {
            try {
                this.loadingChatList = true;
                const t = await APIService.post(`${process.env.VUE_APP_ROOT_API}/command-center-interaction-agent/get-chat-list`, {});
                this.chatList = t.data.chatList;
                this.loadingChatList = false;
            } catch(err) {
                this.chatList = [];
                this.loadingChatList = false;
                console.error(err);
            }
        },
    },
}
</script>

<style scoped>
/* The count gives way first so the Insights Agent button stays on the row when the chat
   panel narrows the dashboard. */
.cc-selected-count {
    flex-shrink: 1;
    min-width: 0;
    height: 40px;
    line-height: 40px;
    color: #858585;
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* Laid out like the SingleSelect and MultiSelect filters next to it. */
.cc-date-filter {
    flex-shrink: 0;
    width: 240px;
    font-family: Arial, Helvetica, sans-serif;
}

.cc-date-filter__label {
    margin: 0 0 4px 2px;
    color: #5f5f5f;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 1px;
}

.cc-date-filter__trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 40px;
    padding: 0 12px;
    border: 1px solid #a8a8a8;
    border-radius: 8px;
    background: #ffffff;
    color: #080808;
    cursor: pointer;
    font: inherit;
    text-align: left;
}

.cc-date-filter__trigger:focus {
    outline: 2px solid #ff4e00;
    outline-offset: 1.5px;
}

.cc-date-filter__trigger--placeholder {
    color: #989898;
}

.cc-date-filter__value {
    margin-right: 8px;
    overflow: hidden;
    font-size: 14px;
    font-weight: 500;
    line-height: 16px;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.cc-date-filter__clear {
    cursor: pointer;
}

.cc-insights {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    flex-shrink: 0;
}

.cc-cycle-label {
    margin-bottom: 4px;
    color: #5f5f5f;
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
}

.cc-insights-btn {
    height: 40px;
    border-radius: 6px;
    white-space: nowrap;
}

.cc-bco {
    padding: 0 16px 12px 14px;
}

.cc-bco__header {
    display: flex;
    align-items: baseline;
    margin: 0 0 6px 2px;
}

.cc-bco__label {
    color: #5f5f5f;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 1px;
}

.cc-bco__toggle-all {
    margin-left: 12px;
    padding: 0;
    border: none;
    background: none;
    color: #ff4e00;
    cursor: pointer;
    font: inherit;
    font-size: 10px;
    font-weight: 600;
}

.cc-bco__toggle-all:hover {
    text-decoration: underline;
}

/* QScrollArea's content box is shrink-to-fit, so the row has to size itself to the
   tiles; the bottom padding keeps the horizontal thumb clear of them. */
.cc-bco__tiles {
    display: flex;
    gap: 8px;
    width: max-content;
    padding: 2px 2px 12px 2px;
}

.cc-bco__tile {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    flex: 0 0 200px;
    min-width: 0;
    padding: 10px 14px;
    border: 1px solid #e3e3e3;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    color: #1f1f1f;
    cursor: pointer;
    font: inherit;
    text-align: left;
    transition: background-color 0.15s, border-color 0.15s, box-shadow 0.15s;
}

.cc-bco__tile:hover {
    border-color: #a8a8a8;
}

.cc-bco__tile:focus-visible {
    outline: 2px solid #ff4e00;
    outline-offset: 1.5px;
}

/* The inset ring thickens the border without changing the tile's size. */
.cc-bco__tile--selected,
.cc-bco__tile--selected:hover {
    border-color: #ff4e00;
    background: #fff1ea;
    box-shadow: inset 0 0 0 1px #ff4e00, 0 1px 4px rgba(255, 78, 0, 0.15);
}

.cc-bco__name {
    display: flex;
    align-items: center;
    width: 100%;
    min-width: 0;
    font-size: 13px;
    font-weight: 700;
    line-height: 16px;
}

.cc-bco__dot {
    flex-shrink: 0;
    width: 7px;
    height: 7px;
    margin-right: 8px;
    border-radius: 50%;
    background: #858585;
}

.cc-bco__dot--agent {
    background: #ff4e00;
}

.cc-bco__dot--existing {
    background: #2e9e4f;
}

.cc-bco__pill {
    margin-top: 6px;
    padding: 1px 6px;
    border-radius: 4px;
    background: #f0f0f0;
    color: #5f5f5f;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.5px;
    line-height: 14px;
    text-transform: uppercase;
}

.cc-bco__pill--agent {
    background: #fff1ea;
    color: #ff4e00;
}

.cc-bco__pill--existing {
    background: #e8f5ec;
    color: #2e7d43;
}

.cc-bco__imperative {
    width: 100%;
    margin-top: 6px;
    color: #858585;
    font-size: 11px;
    line-height: 14px;
}

.cc-bco__empty {
    padding: 8px 2px;
    color: #858585;
    font-size: 12px;
}

.cc-kpi-grid {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: 12px;
}

.cc-row {
    display: grid;
    grid-template-columns: minmax(0, 1.7fr) minmax(0, 1fr);
    gap: 12px;
    align-items: stretch;
}

.cc-row--thirds {
    grid-template-columns: repeat(3, minmax(0, 1fr));
}

.cc-row--full {
    grid-template-columns: minmax(0, 1fr);
}

.cc-kpi-grid--compact {
    grid-template-columns: repeat(4, minmax(0, 1fr));
}

/* The rows dissolve so every card becomes a cell of one grid and can be re-paired. */
.cc-panels--compact {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-template-areas:
        "health health"
        "channel tier"
        "dismiss trend"
        "territory territory";
    gap: 12px;
}

.cc-panels--compact > .cc-row {
    display: contents;
}

.cc-panels--compact .cc-card--health { grid-area: health; }
.cc-panels--compact .cc-card--channel { grid-area: channel; }
.cc-panels--compact .cc-card--tier { grid-area: tier; }
.cc-panels--compact .cc-card--dismiss { grid-area: dismiss; }
.cc-panels--compact .cc-card--trend { grid-area: trend; }
.cc-panels--compact .cc-card--territory { grid-area: territory; }

.cc-card {
    min-width: 0;
    padding: 12px 16px 14px 16px;
    border: 1px solid #dedede;
    border-radius: 8px;
    background: #ffffff;
}

.cc-card--health {
    display: flex;
    flex-direction: column;
}

.cc-card--health .cc-pagination {
    margin-top: auto;
}

.cc-health-info {
    margin-left: 4px;
    vertical-align: text-bottom;
    cursor: help;
}

.cc-card__header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 10px;
}

.cc-card__title {
    color: #262626;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 1px;
}

.cc-card__subtitle {
    color: #9f9f9f;
    font-size: 10px;
}

.cc-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 11px;
}

.cc-table th {
    padding: 6px 8px;
    border-bottom: 1px solid #dedede;
    color: #5f5f5f;
    font-weight: 600;
    white-space: nowrap;
}

.cc-card--territory .cc-table th {
    white-space: normal;
    vertical-align: bottom;
    line-height: 1.3;
}

.cc-card--territory .cc-territory__rep {
    width: 90px;
}

.cc-card--territory .cc-territory__change {
    width: 100px;
}

.cc-table td {
    padding: 8px;
    border-bottom: 1px solid #f0f0f0;
    color: #262626;
}

.cc-table th:first-child,
.cc-table td:first-child {
    padding-left: 0;
}

.cc-table th:last-child,
.cc-table td:last-child {
    padding-right: 0;
}

.cc-table tbody tr:last-child td {
    border-bottom: none;
}

.cc-table__row--declined td {
    background: #fff1ea;
    color: #ff4e00;
    font-weight: 700;
}

.cc-pagination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 6px;
    padding-top: 8px;
    border-top: 1px solid #f0f0f0;
}

.cc-pagination__range {
    color: #858585;
    font-size: 10px;
}

.recommendation-markdown {
    color: #333;
}

.recommendation-markdown :deep(p) {
    display: inline;
    margin: 0;
}

.recommendation-markdown :deep(ul) {
    display: block;
    margin: 4px 0 0;
    padding-left: 18px;
}

.recommendation-markdown :deep(li) {
    line-height: 1.6;
}

.answer-bubble {
    max-width: 90%;
    min-width: 0;
    border: 1px solid #ffd3c0;
    border-radius: 8px;
    background: #fff1ea;
    font-size: 11px;
    color: #b33700;
}

.answer-trust-score {
    float: right;
    margin: -2px -2px 2px 8px;
}

.suggested-question {
    cursor: pointer;
    border: 0.5px solid #424242;
    border-radius: 999px;
    padding: 4px 10px;
    font-size: 10px;
    color: #424242;
    background: #ffffff;
}

.suggested-question:hover {
    border-color: #1f1f1f;
    color: #1f1f1f;
    background: #fff6f0;
}

.chart-thumb-wrap {
    position: relative;
    width: 100%;
    max-width: 100%;
    min-width: 0;
}

.chart-download-btn {
    position: absolute;
    top: 4px;
    right: 28px;
    min-height: 0;
    padding: 2px;
    border: 1px solid #ffd3c0;
    border-radius: 4px;
    color: #5f5f5f;
    background: #ffffff;
}

.chart-download-btn:hover {
    border-color: #ff4e00;
    color: #ff4e00;
}

.chart-thumb {
    position: relative;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    cursor: pointer;
    overflow: hidden;
    border: 1px solid #ffd3c0;
    border-radius: 6px;
    background: #ffffff;
    transition: all 0.1s ease;
}

.chart-thumb:hover {
    border-color: #ff4e00;
}

.chart-thumb-icon {
    position: absolute;
    top: 4px;
    right: 4px;
    padding: 3px;
    border-radius: 4px;
    color: #ffffff;
    background: rgba(0, 0, 0, 0.45);
    opacity: 0;
    transition: opacity 0.1s ease;
}

.chart-thumb-wrap:hover .chart-thumb-icon {
    opacity: 1;
}

.old-chat-row {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 7px 8px;
    margin-top: 4px;
    cursor: pointer;
    border: 1px solid transparent;
    border-radius: 6px;
    background: #ffffff;
    transition: all 0.1s ease;
}

.old-chat-row:hover {
    background: #f7f7f7;
    border-color: #dedede;
}

.old-chat-row--selected {
    background: #fff1ea;
    border-color: #ffd3c0;
}

.old-chat-name {
    flex: 1;
    min-width: 0;
    font-size: 12px;
    color: #414141;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.old-chat-row--selected .old-chat-name {
    color: #b33700;
    font-weight: 600;
}

.old-chat-name-input {
    flex: 1;
    min-width: 0;
    font-family: inherit;
    font-size: 12px;
    color: #414141;
    background: #ffffff;
    border: 1px solid #ff4e00;
    border-radius: 4px;
    padding: 2px 6px;
    outline: none;
}

.old-chat-action-btn {
    opacity: 0;
    transition: opacity 0.1s ease;
}

.old-chat-row:hover .old-chat-action-btn,
.old-chat-action-btn.is-visible {
    opacity: 1;
}

.question-block {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    max-width: 90%;
}

/* QScrollArea's content box is absolutely positioned and shrink-to-fit, so wide
   content (a long line of generated code) stretches it and drags the answer
   bubble past the panel edge. Cap it so children scroll within the panel. */
.chat-scroll :deep(.q-scrollarea__content) {
    max-width: 100%;
}

.question-bubble {
    border-radius: 8px;
    border: 1px solid #dedede;
    color: #414141;
    font-size: 11px;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
}

.question-edit-btn {
    flex: none;
    opacity: 0;
    transition: opacity 0.1s ease;
}

.question-block:hover .question-edit-btn {
    opacity: 1;
}

.question-edit-box {
    width: 90%;
    border-radius: 8px;
    border: 1px solid #ff4e00;
    background: #ffffff;
}

.question-edit-input {
    display: block;
    width: 100%;
    font-family: inherit;
    font-size: 11px;
    line-height: 1.5;
    color: #414141;
    background: #ffffff;
    border: none;
    outline: none;
    padding: 0;
    resize: vertical;
}
</style>
